// A theme, which is a block of token values and nothing else.
//
// 15-branding-and-theming.md sells tier 1 as "colours, logo, typeface, corner
// radius, configuration only, no code, no release". That promise is only true
// if a theme cannot reach anything except the tokens on this list, so the list
// is closed and the values are checked. An open theme is a client stylesheet
// wearing a different name, and the first thing a client stylesheet does is
// make the consent text smaller.
//
// Two things are deliberately not themeable and both are refused with a
// specific message rather than ignored: touch target size and type scale.
// Section 4 locks them at every tier. A brand that is paying more does not get
// a smaller target.

import type { CSSProperties } from "react";

/** What a client theme may set. Mirrors the themeable group in tokens.css. */
export const THEMEABLE_TOKENS = [
  "--xp-bg",
  "--xp-surface",
  "--xp-surface-2",
  "--xp-line",
  "--xp-line-strong",
  "--xp-ink",
  "--xp-ink-2",
  "--xp-ink-3",
  "--xp-accent",
  "--xp-accent-ink",
  "--xp-data",
  "--xp-tint",
  "--xp-good",
  "--xp-good-soft",
  "--xp-warn",
  "--xp-warn-soft",
  "--xp-bad",
  "--xp-bad-soft",
  "--xp-radius",
  "--xp-radius-sm",
  "--xp-sans",
  "--xp-shadow",
  "--xp-scrim",
] as const;

/** What no theme may set, at any tier. Mirrors the locked group in tokens.css. */
export const LOCKED_TOKENS = [
  "--xp-touch-min",
  "--xp-touch-primary",
  "--xp-touch-gap",
  "--xp-text-s",
  "--xp-text-m",
  "--xp-text-l",
  "--xp-text-xl",
  "--xp-text-xxl",
  "--xp-leading",
  "--xp-leading-tight",
  "--xp-space-1",
  "--xp-space-2",
  "--xp-space-3",
  "--xp-space-4",
  "--xp-space-5",
  "--xp-space-6",
  "--xp-focus-width",
  "--xp-focus-offset",
] as const;

export type ThemeToken = (typeof THEMEABLE_TOKENS)[number];
export type LockedToken = (typeof LOCKED_TOKENS)[number];

export type Theme = Partial<Record<ThemeToken, string>>;

export type ThemeProblem = {
  token: string;
  message: string;
};

const THEMEABLE = new Set<string>(THEMEABLE_TOKENS);
const LOCKED = new Set<string>(LOCKED_TOKENS);

export const isThemeToken = (name: string): name is ThemeToken => THEMEABLE.has(name);

// A theme arrives inside a signed bundle, so it is not anonymous input, but it
// is still authored by whoever handled the client's brand guidelines and it
// ends up in a style attribute. Everything a declaration could be terminated
// or escaped with is refused, and so is anything that fetches.
const SAFE_VALUE = /^[#a-zA-Z0-9 .,%()'"/_-]+$/;
const FORBIDDEN = [";", "{", "}", "<", ">", "\\", "@", "url(", "expression", "/*", "!important"];
const MAX_VALUE_LENGTH = 200;

/**
 * Everything wrong with a theme, rather than the first thing wrong with it.
 *
 * The caller is a theme compiler running before an activation opens, and a
 * compiler that reports one problem per run turns a brand conversation into
 * four of them.
 */
export function checkTheme(input: Readonly<Record<string, string>>): ThemeProblem[] {
  const problems: ThemeProblem[] = [];

  for (const [token, value] of Object.entries(input)) {
    if (LOCKED.has(token)) {
      problems.push({
        token,
        message:
          "this token is locked at every tier. Touch target size and type scale are an accessibility minimum, not a brand decision",
      });
      continue;
    }
    if (!THEMEABLE.has(token)) {
      problems.push({ token, message: "not a token. A theme sets tokens and nothing else" });
      continue;
    }
    if (value.length > MAX_VALUE_LENGTH) {
      problems.push({ token, message: `value is longer than ${MAX_VALUE_LENGTH} characters` });
      continue;
    }
    const bad = FORBIDDEN.find((f) => value.includes(f));
    if (bad !== undefined) {
      problems.push({ token, message: `value contains ${JSON.stringify(bad)}, which a token value never needs` });
      continue;
    }
    if (!SAFE_VALUE.test(value)) {
      problems.push({ token, message: "value contains a character a token value never needs" });
    }
  }

  return problems;
}

/**
 * A theme as custom properties for the `style` attribute of one element.
 *
 * Scoped rather than written to `:root` on purpose. Two activations previewed
 * side by side, and the consent presenter embedded in a client's own page under
 * tier 3, both need a theme that stops at the element it was given to.
 */
export function themeStyle(theme: Theme): CSSProperties {
  const style: Record<string, string> = {};
  for (const token of THEMEABLE_TOKENS) {
    const value = theme[token];
    if (value !== undefined) style[token] = value;
  }
  // csstype does not model custom properties, and React sets these through
  // CSSStyleDeclaration.setProperty regardless of what the type says.
  return style as CSSProperties;
}

/** The same theme as a stylesheet, for a surface that wants it on one selector. */
export function themeToCss(theme: Theme, selector = ":root"): string {
  const lines = THEMEABLE_TOKENS.filter((t) => theme[t] !== undefined).map(
    (t) => `  ${t}: ${theme[t]};`,
  );
  return `${selector} {\n${lines.join("\n")}\n}\n`;
}

// ------------------------------------------------------------------ contrast

/** #rgb, #rrggbb or #rrggbbaa. Anything else is not measurable here. */
export function parseHexColour(value: string): [number, number, number] | null {
  const hex = value.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;

  let full: string;
  if (hex.length === 3) full = hex.split("").map((c) => c + c).join("");
  else if (hex.length === 6 || hex.length === 8) full = hex.slice(0, 6);
  else return null;

  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return [r, g, b];
}

const channel = (v: number): number => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: string, b: string): number | null {
  const one = parseHexColour(a);
  const two = parseHexColour(b);
  if (!one || !two) return null;
  const la = relativeLuminance(one);
  const lb = relativeLuminance(two);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The pairings that have to hold whatever a brand supplies.
 *
 * `least` is 4.5 where the pair is text being read and 3 where it is a shape
 * being seen. Nothing here is above the AA line: a theme that only just clears
 * it is already an argument worth having with the client, and refusing at AAA
 * would refuse most real brand palettes and get the check switched off.
 *
 * All three ink weights are measured on both grounds, and the reason is worth
 * stating because the list was missing one until check.mjs compared it to
 * xp1-design.md. The page is the darker of the two grounds, so an ink weight
 * that clears 4.5:1 on a card can still miss it on the page, and the pair left
 * out was the stricter one rather than the redundant one.
 */
export const CONTRAST_PAIRS: ReadonlyArray<{
  what: string;
  ink: ThemeToken;
  on: ThemeToken;
  least: number;
}> = [
  { what: "body text on a card", ink: "--xp-ink", on: "--xp-surface", least: 4.5 },
  { what: "body text on the page", ink: "--xp-ink", on: "--xp-bg", least: 4.5 },
  { what: "help text on a card", ink: "--xp-ink-2", on: "--xp-surface", least: 4.5 },
  { what: "help text on the page", ink: "--xp-ink-2", on: "--xp-bg", least: 4.5 },
  { what: "quiet text on a card", ink: "--xp-ink-3", on: "--xp-surface", least: 4.5 },
  { what: "quiet text on the page", ink: "--xp-ink-3", on: "--xp-bg", least: 4.5 },
  { what: "the label on a primary button", ink: "--xp-accent-ink", on: "--xp-accent", least: 4.5 },
  { what: "a validation message", ink: "--xp-bad", on: "--xp-surface", least: 4.5 },
  { what: "a warning message", ink: "--xp-warn", on: "--xp-surface", least: 4.5 },
  { what: "a confirmation", ink: "--xp-good", on: "--xp-surface", least: 4.5 },
  { what: "text on a chosen option", ink: "--xp-ink", on: "--xp-tint", least: 4.5 },
  { what: "the focus ring against the page", ink: "--xp-accent", on: "--xp-bg", least: 3 },
  { what: "a field border against a card", ink: "--xp-line-strong", on: "--xp-surface", least: 3 },
];

/**
 * Refuse an unreadable theme rather than ship it and find out on the morning.
 *
 * Takes the resolved token set, meaning the client's overrides already merged
 * over the defaults in tokens.css. A partial theme cannot be judged: a brand
 * that supplies only an accent still has to be measured against the ink it
 * will carry.
 */
export function checkThemeContrast(resolved: Readonly<Record<string, string>>): ThemeProblem[] {
  const problems: ThemeProblem[] = [];

  for (const pair of CONTRAST_PAIRS) {
    const ink = resolved[pair.ink];
    const on = resolved[pair.on];
    if (ink === undefined || on === undefined) continue;

    const ratio = contrastRatio(ink, on);
    if (ratio === null) {
      problems.push({
        token: pair.ink,
        message: `${pair.what}: contrast cannot be measured. Supply brand colours as hex, per 15-branding-and-theming.md section 7`,
      });
      continue;
    }
    if (ratio < pair.least) {
      problems.push({
        token: pair.ink,
        message: `${pair.what}: ${ratio.toFixed(2)}:1 against ${pair.on}, and it has to reach ${pair.least}:1`,
      });
    }
  }

  return problems;
}
